import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Warning,
  Error,
  Info,
  ExpandMore,
  ExpandLess,
  Refresh,
  PlayArrow,
  Stop,
  Lock,
  LockOpen,
  Public,
  VpnLock,
  Router,
  Storage,
  DevicesOther,
  Cloud,
} from '@mui/icons-material';
import { useActivity } from '../../contexts/ActivityContext';

const SecurityAudit = () => {
  const [target, setTarget] = useState('');
  const [scanType, setScanType] = useState('quick');
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const { updateActivity } = useActivity();

  const scanTypes = [
    { value: 'quick', label: 'Quick Scan (Basic checks)' },
    { value: 'full', label: 'Full Scan (Comprehensive)' },
    { value: 'web', label: 'Web Application Scan' },
    { value: 'network', label: 'Network Vulnerability Scan' },
  ];

  const severityIcons = {
    critical: <Error color="error" />,
    high: <Warning color="warning" />,
    medium: <Info color="info" />,
    low: <Info color="info" />,
    info: <CheckCircle color="success" />,
  };

  const severityColors = {
    critical: 'error',
    high: 'warning',
    medium: 'info',
    low: 'info',
    info: 'success',
  };

  const handleScan = async () => {
    if (!target) {
      showSnackbar('Please enter a target to scan', 'error');
      return;
    }

    setIsScanning(true);
    setProgress(0);
    setResults(null);
    updateActivity('Security Audit', `Started ${scanType} scan on ${target}`);

    // Simulate scan progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + Math.floor(Math.random() * 10) + 5;
        if (newProgress >= 100) {
          clearInterval(interval);
          completeScan();
          return 100;
        }
        return newProgress;
      });
    }, 500);

    // In a real app, this would be an API call to your backend
    const completeScan = () => {
      setTimeout(() => {
        setResults(generateMockResults());
        setIsScanning(false);
        updateActivity('Security Audit', `Completed ${scanType} scan on ${target}`, { findings: 5 });
      }, 1000);
    };
  };

  const generateMockResults = () => ({
    summary: {
      target,
      timestamp: new Date().toISOString(),
      duration: '2m 34s',
      totalChecks: 42,
      vulnerabilities: {
        critical: Math.floor(Math.random() * 3),
        high: Math.floor(Math.random() * 5) + 1,
        medium: Math.floor(Math.random() * 8) + 2,
        low: Math.floor(Math.random() * 10) + 5,
      },
    },
    findings: [
      {
        id: 'ssl-weak-cipher',
        title: 'Weak SSL/TLS Cipher Suites',
        severity: 'critical',
        category: 'Encryption',
        description: 'The server supports weak or outdated SSL/TLS cipher suites that could be exploited by attackers to perform man-in-the-middle attacks.',
        impact: 'Attackers could potentially decrypt sensitive information in transit.',
        recommendation: 'Disable weak cipher suites and use only strong, modern encryption protocols (TLS 1.2/1.3).',
        references: [
          'https://owasp.org/www-community/controls/Certificate_and_Public_Key_Pinning',
          'https://ssl-config.mozilla.org/',
        ],
        cve: ['CVE-2016-2183', 'CVE-2016-6309'],
        cvss: 8.2,
      },
      // Add more mock findings...
    ],
  });

  const handleExpand = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getSeverityCount = (severity) => {
    return results?.findings.filter((f) => f.severity === severity).length || 0;
  };

  const filteredFindings = results?.findings.filter(
    (finding) => !showCriticalOnly || finding.severity === 'critical' || finding.severity === 'high'
  );

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3, background: 'rgba(30, 30, 45, 0.7)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Security Audit Configuration
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Target (IP, Domain, or URL)"
                variant="outlined"
                size="small"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                disabled={isScanning}
                placeholder="example.com or 192.168.1.1"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Scan Type</InputLabel>
                <Select
                  value={scanType}
                  label="Scan Type"
                  onChange={(e) => setScanType(e.target.value)}
                  disabled={isScanning}
                >
                  {scanTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isScanning ? <Stop /> : <PlayArrow />}
                onClick={isScanning ? () => setIsScanning(false) : handleScan}
                disabled={!target}
                fullWidth
              >
                {isScanning ? 'Stop Scan' : 'Start Scan'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setTarget('');
                  setResults(null);
                  setProgress(0);
                }}
                disabled={isScanning}
              >
                <Refresh />
              </Button>
            </Grid>
          </Grid>
          {isScanning && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" color="text.secondary">
                Scanning in progress... {progress}%
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {results && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Scan Summary
              </Typography>
              <Typography variant="h4" color="primary">
                {results.summary.target}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(results.summary.timestamp).toLocaleString()}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <strong>Duration:</strong> {results.summary.duration}
                <br />
                <strong>Checks Performed:</strong> {results.summary.totalChecks}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: 'error.dark' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="error.contrastText">
                  Critical
                </Typography>
                <Chip
                  label={results.summary.vulnerabilities.critical}
                  size="small"
                  sx={{ bgcolor: 'error.contrastText', color: 'error.dark', fontWeight: 'bold' }}
                />
              </Box>
              <Typography variant="h4" color="error.contrastText" sx={{ mt: 1 }}>
                {results.summary.vulnerabilities.critical}
              </Typography>
              <Typography variant="caption" color="error.contrastText" sx={{ opacity: 0.8 }}>
                Immediate attention required
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: 'warning.dark' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="warning.contrastText">
                  High
                </Typography>
                <Chip
                  label={results.summary.vulnerabilities.high}
                  size="small"
                  sx={{ bgcolor: 'warning.contrastText', color: 'warning.dark', fontWeight: 'bold' }}
                />
              </Box>
              <Typography variant="h4" color="warning.contrastText" sx={{ mt: 1 }}>
                {results.summary.vulnerabilities.high}
              </Typography>
              <Typography variant="caption" color="warning.contrastText" sx={{ opacity: 0.8 }}>
                High priority issues
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: 'info.dark' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="info.contrastText">
                  Medium/Low
                </Typography>
                <Chip
                  label={
                    results.summary.vulnerabilities.medium + results.summary.vulnerabilities.low
                  }
                  size="small"
                  sx={{ bgcolor: 'info.contrastText', color: 'info.dark', fontWeight: 'bold' }}
                />
              </Box>
              <Typography variant="h4" color="info.contrastText" sx={{ mt: 1 }}>
                {results.summary.vulnerabilities.medium + results.summary.vulnerabilities.low}
              </Typography>
              <Typography variant="caption" color="info.contrastText" sx={{ opacity: 0.8 }}>
                Review recommended
              </Typography>
            </Paper>
          </Box>

          <Card variant="outlined" sx={{ mb: 3, background: 'rgba(30, 30, 45, 0.7)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Vulnerability Findings</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCriticalOnly}
                      onChange={(e) => setShowCriticalOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Show Critical/High Only"
                />
              </Box>

              <List>
                {filteredFindings?.map((finding) => (
                  <React.Fragment key={finding.id}>
                    <ListItem
                      button
                      onClick={() => handleExpand(finding.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {severityIcons[finding.severity]}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" component="span">
                              {finding.title}
                            </Typography>
                            <Chip
                              label={finding.severity.toUpperCase()}
                              size="small"
                              color={severityColors[finding.severity]}
                              variant="outlined"
                            />
                            {finding.cve?.length > 0 && (
                              <Chip
                                label={`CVE-${finding.cve[0]}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={`CVSS: ${finding.cvss}/10`}
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={finding.description.substring(0, 120) + '...'}
                      />
                      {expanded[finding.id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={expanded[finding.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 8, pr: 2, pb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Impact:
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {finding.impact}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          Recommendation:
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {finding.recommendation}
                        </Typography>

                        {finding.references && finding.references.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>
                              References:
                            </Typography>
                            <List dense>
                              {finding.references.map((ref, idx) => (
                                <ListItem key={idx} disableGutters>
                                  <ListItemText
                                    primary={
                                      <a
                                        href={ref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#90caf9' }}
                                      >
                                        {ref}
                                      </a>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ background: 'rgba(30, 30, 45, 0.7)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Remediation Plan
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Lock color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Immediate Actions (Critical/High)"
                    secondary="Address these vulnerabilities within 24-48 hours"
                  />
                  <Chip
                    label={`${getSeverityCount('critical') + getSeverityCount('high')} items`}
                    color="error"
                    variant="outlined"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LockOpen color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Recommended Updates (Medium)"
                    secondary="Address within 7-14 days"
                  />
                  <Chip
                    label={`${getSeverityCount('medium')} items`}
                    color="warning"
                    variant="outlined"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Info color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Informational (Low)"
                    secondary="Review during next maintenance window"
                  />
                  <Chip
                    label={`${getSeverityCount('low')} items`}
                    color="info"
                    variant="outlined"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SecurityAudit;
